import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import '@treasury/omega/components/progress/omega-progress';
import { TmBaseComponent } from '@treasury/presentation';
import { hideIcon, showIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-password-field';
import '@treasury/presentation/components/tm-text-field';
import { css, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'treasury-login';
@customElement(tagName)
export default class Login extends TmBaseComponent {
    constructor() {
        super();
        this.addEventListener('keydown', (e: KeyboardEvent) => {
            this.onKeyDown(e);
        });
    }

    @property({ type: Boolean })
    public loading = false;

    @state()
    private companyId = '';

    @state()
    private loginId = '';

    @state()
    private password = '';

    @state()
    private institutionId = '';

    @state()
    private passwordInputType: 'password' | 'text' = 'password';

    @state()
    private showPasswordLabel: 'Show Password' | 'Hide Password' = 'Show Password';

    @state()
    private passwordIcon: TemplateResult = showIcon;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    private get isValid() {
        return this.companyId && this.loginId && this.password;
    }

    protected render() {
        return html`
            <div id="content" class="flex flex-col justify-end h-full">
                <div class="m-4">
                    ${this.isConsumer
                        ? html`<tm-text-field
                              label="Company ID"
                              aria-label="Company ID"
                              .initial=${this.companyId}
                              @value-changed=${(e: CustomEvent) => {
                                  this.companyId = e.detail.value;
                              }}
                              .required=${this.isConsumer}
                              autocapitalize="off"
                          ></tm-text-field>`
                        : nothing}
                    ${this.isBackOffice
                        ? html`<tm-text-field
                              label="FI ID"
                              aria-label="FI ID"
                              .initial=${this.institutionId}
                              @value-changed=${(e: CustomEvent) => {
                                  this.institutionId = e.detail.value;
                              }}
                              .required=${this.isBackOffice}
                              autocapitalize="off"
                          ></tm-text-field>`
                        : nothing}
                    <tm-text-field
                        label="Login ID"
                        aria-label="Login ID"
                        .initial=${this.loginId}
                        @value-changed=${(e: CustomEvent) => {
                            this.loginId = e.detail.value;
                        }}
                        required
                        class="pb-1"
                        autocapitalize="off"
                    >
                    </tm-text-field>
                    <tm-password-field
                        label="Password"
                        aria-label="Password"
                        .initial=${this.password}
                        @value-changed=${(e: CustomEvent) => {
                            this.password = e.detail.value;
                        }}
                        required
                        class="pb-1"
                        autocapitalize="off"
                    >
                        <button
                            slot="input-right"
                            aria-label="${this.showPasswordLabel}"
                            @click=${() => this.togglePasswordType()}
                        >
                            <jhd-icon .icon=${this.passwordIcon}></jhd-icon>
                        </button>
                    </tm-password-field>
                </div>
                <tm-footer
                    .buttonConfig=${[
                        {
                            text: 'Login',
                            onClick: () => this.login(),
                            disabled: this.isDisabled(),
                        },
                        {
                            importance: 'tertiary',
                            text: 'Forgot Password?',
                            onClick: () => {
                                this.navigationService.navigate(`/forgot-password`);
                            },
                        },
                    ] as ButtonConfig[]}
                >
                </tm-footer>
            </div>
        `;
    }

    private login() {
        const { companyId, loginId, password, institutionId } = this;
        const formData = {
            companyId,
            loginId,
            password,
            institutionId,
        };
        this.dispatchEvent(new CustomEvent('login', { detail: formData }));
    }

    private onKeyDown(e: KeyboardEvent) {
        if (e.code === 'Enter' && this.isValid) {
            this.login();
        }
    }

    private togglePasswordType() {
        if (this.passwordInputType === 'password') {
            this.passwordInputType = 'text';
            this.passwordIcon = hideIcon;
            this.showPasswordLabel = 'Hide Password';
        } else {
            this.passwordInputType = 'password';
            this.passwordIcon = showIcon;
            this.showPasswordLabel = 'Show Password';
        }
    }

    private get isBackOffice() {
        return this.config.app === AppType.BackOffice;
    }

    private get isConsumer() {
        return this.config.app !== AppType.BackOffice;
    }

    private isDisabled() {
        if (this.isConsumer) {
            return !this.companyId || !this.loginId || !this.password;
        }
        return !this.institutionId || !this.loginId || !this.password;
    }

    public static get styles() {
        return [
            css`
                :host() {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                :host([stickyheader]) {
                    display: block !important;
                }

                :host([stickyheader]) #content {
                    padding-bottom: var(--sticky-header-height, 0);
                }

                tm-footer {
                    padding-top: 1em;
                }

                @media only screen and (min-width: 640px) {
                    :host([stickyheader]) #content {
                        padding-bottom: 0px;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: Login;
    }
}
